import React from 'react';

import * as ApolloReactHooks from '@apollo/client';

import get from 'lodash/get';
import set from 'lodash/set';
import merge from 'lodash/merge';

type UpdateQueryFn<Result> = (
  previousQueryResult: Result,
  { fetchMoreResult }: { fetchMoreResult?: Result }
) => Result;

export interface UseListParams<Result, Variable extends ApolloReactHooks.OperationVariables>
  extends ApolloReactHooks.QueryFunctionOptions<Result, Variable> {
  queryHook: (
    baseOptions: ApolloReactHooks.QueryHookOptions<Result, Variable>
  ) => ApolloReactHooks.QueryResult<Result, Variable>;
  getVars: (nextCursor?: string) => Variable;
  cursorPath: string;
  dataPath: string;
  updateQuery?: UpdateQueryFn<Result>;
}

export interface UseListResult<Result, Variable extends ApolloReactHooks.OperationVariables>
  extends ApolloReactHooks.QueryResult<Result, Variable> {
  canLoadMore: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
}

export function useList<Result, Variable extends ApolloReactHooks.OperationVariables>(
  props: UseListParams<Result, Variable>
): UseListResult<Result, Variable> {
  const lock = React.useRef(false);
  const { queryHook, getVars, cursorPath, dataPath, updateQuery, ...options } = props;

  const getUpdateQuery =
    (isRefresh = false) =>
    (previousQueryResult: Result, { fetchMoreResult }: { fetchMoreResult?: Result }): Result => {
      if (!fetchMoreResult) {
        return previousQueryResult;
      } else if (isRefresh) {
        return fetchMoreResult;
      }

      if (updateQuery) {
        return updateQuery(previousQueryResult, { fetchMoreResult });
      }

      const result = merge({}, previousQueryResult, fetchMoreResult);

      set(result, dataPath, [
        ...(get(previousQueryResult, dataPath, []) as unknown[]),
        ...(get(fetchMoreResult, dataPath, []) as unknown[]),
      ]);

      return result;
    };

  const result: UseListResult<Result, Variable> = queryHook({
    variables: getVars(),
    ...options,
  }) as UseListResult<Result, Variable>;

  const { loading, fetchMore } = result;
  const nextCursor = get(result, 'data.' + cursorPath, '');
  const canLoadMore = Boolean(nextCursor);
  const fetch =
    (isRefresh = false) =>
    async (): Promise<void> => {
      if (loading || (!canLoadMore && !isRefresh) || lock.current) {
        return;
      }
      lock.current = true;
      await fetchMore({
        variables: getVars(isRefresh ? undefined : nextCursor),
        updateQuery: getUpdateQuery(isRefresh),
      });
      setTimeout(() => {
        lock.current = false;
      }, 50);
    };

  result.canLoadMore = canLoadMore;
  result.onLoadMore = fetch();
  result.onRefresh = fetch(true);

  return result;
}
